import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, Phone, Mail, MapPin, Palette, Calendar as CalIcon, Send, Sparkles, Instagram, ChevronRight, Wallet, Check, X } from "lucide-react";
import { api } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { CornerLeaves, SectionFlourish, Sprig, HeartDivider } from "@/components/Botanical";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 1.1, ease: [0.16, 1, 0.3, 1] },
};

const PETALS = Array.from({ length: 16 });

const FloatingPetals = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden>
    {PETALS.map((_, i) => {
      const size = 6 + Math.random() * 12;
      const left = Math.random() * 100;
      const dur = 18 + Math.random() * 22;
      const delay = -Math.random() * dur;
      const colors = ["#C5868A", "#F5CFCE", "#E8B4B5", "#FBE5E5", "#B58A8E"];
      const bg = colors[i % colors.length];
      return (
        <span
          key={i}
          className="petal"
          style={{
            left: `${left}%`,
            top: `-30px`,
            width: `${size}px`,
            height: `${size}px`,
            background: bg,
            animationDuration: `${dur}s`,
            animationDelay: `${delay}s`,
          }}
        />
      );
    })}
  </div>
);

const SectionTitle = ({ overline, script, title, sub, dark = false }) => (
  <motion.div {...fadeUp} className="text-center max-w-2xl mx-auto mb-16 md:mb-20">
    {overline && <p className={`overline mb-5 ${dark ? "text-rose" : "text-rose"}`}>{overline}</p>}
    {script && <p className="font-script text-5xl md:text-6xl text-rose leading-none mb-2">{script}</p>}
    <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-light tracking-tight leading-[1.05] text-ink">
      {title}
    </h2>
    {sub && <p className="mt-6 text-base md:text-lg text-ink-soft font-light leading-relaxed">{sub}</p>}
    <SectionFlourish className="w-48 h-6 mx-auto mt-8" />
  </motion.div>
);

const ReviewModal = ({ open, onClose, onSuccess }) => {
  const [form, setForm] = useState({ customer_name: "", event_type: "", quote: "", rating: 5 });
  const [state, setState] = useState("idle"); // idle | submitting | done | error
  if (!open) return null;
  const submit = async (e) => {
    e.preventDefault();
    setState("submitting");
    try {
      await api.post("/reviews", form);
      setState("done");
      if (onSuccess) onSuccess();
    } catch { setState("error"); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-ink/50 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="frame-gold bg-ivory p-8 md:p-12 w-full max-w-lg relative" data-testid="review-modal">
        <button onClick={onClose} className="absolute top-4 right-4 p-2 hover:text-rose" data-testid="review-close" aria-label="Close">
          <X size={18} strokeWidth={1.25} />
        </button>
        {state !== "done" ? (
          <>
            <p className="overline text-rose text-center">Kind Words</p>
            <p className="font-pinyon text-5xl text-rose text-center leading-none mt-2">Leave a Review</p>
            <SectionFlourish className="w-40 h-5 mx-auto my-6" />
            <form onSubmit={submit} className="space-y-5">
              <div>
                <label className="overline">Your Name *</label>
                <input required className="elegant-input" value={form.customer_name} onChange={e => setForm({ ...form, customer_name: e.target.value })} data-testid="review-name" />
              </div>
              <div>
                <label className="overline">Event Type</label>
                <input className="elegant-input" placeholder="Wedding · Birthday · Bridal Shower…" value={form.event_type} onChange={e => setForm({ ...form, event_type: e.target.value })} data-testid="review-event" />
              </div>
              <div>
                <label className="overline">Your Review *</label>
                <textarea required rows={4} className="elegant-input resize-none" value={form.quote} onChange={e => setForm({ ...form, quote: e.target.value })} data-testid="review-quote" />
              </div>
              <div>
                <label className="overline block mb-3">Rating</label>
                <div className="flex gap-2 justify-center">
                  {[1,2,3,4,5].map(n => (
                    <button type="button" key={n} onClick={() => setForm({ ...form, rating: n })} data-testid={`review-star-${n}`} className="p-1">
                      <Star size={26} strokeWidth={1} className={n <= form.rating ? "fill-rose text-rose" : "text-hairline"} />
                    </button>
                  ))}
                </div>
              </div>
              {state === "error" && <p className="text-sm text-destructive text-center">Something went wrong. Please try again.</p>}
              <button type="submit" disabled={state === "submitting"} className="btn-pill-pink w-full justify-center" data-testid="review-submit">
                <Sparkles size={14} strokeWidth={1.5} /> {state === "submitting" ? "Submitting…" : "Post Review"}
              </button>
              <p className="text-xs text-ink-soft text-center italic">Your review will appear on the site right away.</p>
            </form>
          </>
        ) : (
          <div className="text-center py-6" data-testid="review-thankyou">
            <div className="w-14 h-14 bg-rose text-ivory flex items-center justify-center mx-auto mb-6 rounded-full">
              <Check size={22} strokeWidth={1.25} />
            </div>
            <p className="font-pinyon text-5xl text-rose leading-none">Thank you</p>
            <HeartDivider className="w-40 h-5 mx-auto my-6" />
            <p className="text-ink-soft font-light">Your review has been posted and is now live on the site.</p>
            <button onClick={onClose} className="btn-pill-pink mt-8" data-testid="review-close-done">Close</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default function Home() {
  const [settings, setSettings] = useState(null);
  const [bouquets, setBouquets] = useState([]);
  const [dessert, setDessert] = useState(null);
  const [testimonials, setTestimonials] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [instagram, setInstagram] = useState([]);
  const [parallax, setParallax] = useState(0);
  const [reviewOpen, setReviewOpen] = useState(false);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [s, b, d, t, g, ig] = await Promise.all([
          api.get("/settings"),
          api.get("/bouquets"),
          api.get("/dessert-of-month"),
          api.get("/testimonials"),
          api.get("/gallery?category=gallery"),
          api.get("/gallery?category=instagram"),
        ]);
        setSettings(s.data);
        setBouquets(b.data);
        setDessert(d.data);
        setTestimonials(t.data);
        setGallery(g.data);
        setInstagram(ig.data);
      } catch (e) {
        if (process.env.NODE_ENV === "development") console.error(e);
      }
    };
    fetchAll();
  }, []);

  useEffect(() => {
    const onScroll = () => setParallax(window.scrollY);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="bg-ivory min-h-screen">
      <Navbar logoUrl={settings?.logo_url} />

      {/* ======= HERO — soft pink watercolor with gold botanical frame ======= */}
      <section data-testid="hero-section" className="relative min-h-[100vh] flex items-center overflow-hidden pt-24 bg-watercolor">
        <FloatingPetals />
        <CornerLeaves className="absolute top-20 left-2 md:left-6 w-32 md:w-44 opacity-80" />
        <CornerLeaves className="absolute bottom-6 right-2 md:right-6 w-32 md:w-44 opacity-80" flip />

        <div className="relative max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20 py-20 grid lg:grid-cols-12 gap-10 items-center w-full">
          <div className="lg:col-span-7 text-center lg:text-left">
            <motion.p
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.0, delay: 0.1 }}
              className="overline text-rose mb-6"
            >
              <span className="heart" />Mooresville, North Carolina<span className="heart-after" />
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.4, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
              className="relative"
            >
              <p className="font-pinyon text-6xl md:text-8xl lg:text-[8.5rem] text-rose leading-[0.85] tracking-tight">
                Tammy's Treats
              </p>
              <p className="overline text-gold tracking-[0.5em] mt-2 md:mt-4">L U X U R Y</p>
            </motion.div>

            <SectionFlourish className="w-48 h-6 mx-auto lg:mx-0 mt-6" />

            <motion.h1
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.2, delay: 0.45 }}
              className="font-display italic text-3xl md:text-4xl lg:text-5xl font-light text-ink mt-8 leading-tight"
            >
              Edible floral cupcakes,
              <br />
              custom orders available.
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.2, delay: 0.6 }}
              className="mt-6 text-base md:text-lg text-ink-soft font-light max-w-xl mx-auto lg:mx-0 leading-relaxed"
            >
              Hand-piped buttercream bouquets and floral cupcake boxes — made petal by petal, for weddings and the celebrations worth keeping.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.2, delay: 0.8 }}
              className="mt-12 flex flex-wrap items-center justify-center lg:justify-start gap-5"
            >
              <Link to="/inquiry" data-testid="hero-inquiry-btn" className="btn-pill-pink">
                <Sparkles size={14} strokeWidth={1.5} /> Order Inquiry
              </Link>
              <a href="#featured" data-testid="hero-explore-btn" className="elegant-link text-xs uppercase tracking-[0.22em] text-ink">
                Explore Bouquets →
              </a>
            </motion.div>
          </div>

          <div className="lg:col-span-5 hidden lg:block">
            <motion.div
              initial={{ opacity: 0, scale: 1.05 }} animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.6, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="relative gold-corner p-8 bg-ivory/40"
              style={{ transform: `translateY(${-parallax * 0.08}px)` }}
            >
              <div className="img-hover aspect-[3/4]">
                <img src={bouquets[0]?.image_url} alt="Featured bouquet" />
              </div>
              <div className="absolute -bottom-6 -left-6 frame-gold px-7 py-5 max-w-[240px] text-center">
                <p className="overline text-rose">Signature</p>
                <p className="font-script text-3xl text-ink mt-1 leading-none">Cupcake Bouquets</p>
                <p className="text-xs text-ink-soft mt-2">handcrafted · elegant · edible</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ======= FEATURED BOUQUETS — cream cards on watercolor wash ======= */}
      <section id="featured" data-testid="featured-section" className="py-24 md:py-32 lg:py-40 px-6 md:px-12 lg:px-20 bg-watercolor-soft relative overflow-hidden">
        <CornerLeaves className="absolute top-8 right-4 w-32 opacity-50" flip />
        <CornerLeaves className="absolute bottom-8 left-4 w-32 opacity-50" />
        <div className="max-w-[1440px] mx-auto relative">
          <SectionTitle
            overline="The Collection"
            script="Cupcake Bouquets"
            title="Handcrafted. Elegant. Edible."
            sub="Each arrangement is hand-piped to order, petal by petal, in the seasonal palette of your choosing."
          />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
            {bouquets.map((b, i) => (
              <motion.div
                key={b.id}
                {...fadeUp}
                transition={{ ...fadeUp.transition, delay: i * 0.1 }}
                className={`relative ${i % 2 === 1 ? "lg:mt-12" : ""}`}
                data-testid={`bouquet-card-${i}`}
              >
                <div className="gold-corner p-5 bg-ivory">
                  <div className="img-hover aspect-[4/5]">
                    <img src={b.image_url} alt={b.caption || b.name} />
                  </div>
                </div>
                <div className="mt-6 text-center px-4">
                  <p className="overline text-rose mb-2">No. 0{i + 1}</p>
                  <h3 className="font-script text-3xl text-ink leading-none">{b.name}</h3>
                  <Sprig className="w-16 h-5 mx-auto my-4 opacity-70" />
                  <p className="text-sm text-ink-soft font-light leading-relaxed max-w-xs mx-auto">{b.description}</p>
                  <p className="font-display text-xl text-rose mt-5 tracking-wide">{b.price_range}</p>
                </div>
              </motion.div>
            ))}
          </div>
          <div className="mt-20 text-center">
            <Link to="/inquiry" data-testid="featured-cta" className="btn-pill-pink">
              <span className="heart" /> Inquire About a Bouquet
            </Link>
          </div>
        </div>
      </section>

      <HeartDivider className="w-64 h-5 mx-auto my-6" />

      {/* ======= DESSERT OF THE MONTH ======= */}
      {dessert?.title && (
        <section id="dessert" data-testid="dessert-section" className="py-24 md:py-32 lg:py-40 px-6 md:px-12 lg:px-20 bg-cream-soft relative">
          <div className="max-w-[1440px] mx-auto grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
            <motion.div {...fadeUp} className="gold-corner p-6 bg-ivory order-2 lg:order-1">
              <div className="img-hover aspect-[4/5]">
                <img src={dessert.image_url} alt={dessert.title} />
              </div>
            </motion.div>
            <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.2 }} className="order-1 lg:order-2 text-center lg:text-left">
              <p className="overline text-rose mb-4">{dessert.subtitle || "Dessert of the Month"}</p>
              <p className="font-script text-5xl md:text-6xl text-rose leading-none mb-2">Spotlight</p>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-light leading-tight text-ink mt-2">{dessert.title}</h2>
              <SectionFlourish className="w-40 h-5 my-8 mx-auto lg:mx-0" />
              <p className="text-base md:text-lg text-ink-soft font-light leading-relaxed max-w-lg mx-auto lg:mx-0">{dessert.description}</p>
              <p className="mt-8 font-display text-2xl text-rose">{dessert.price_range}</p>
              <Link to="/inquiry" data-testid="dessert-inquiry-btn" className="btn-pill-pink mt-10">
                <span className="heart" />Reserve This Bouquet
              </Link>
            </motion.div>
          </div>
        </section>
      )}

      {/* ======= ABOUT TAMMY ======= */}
      <section id="about" data-testid="about-section" className="py-24 md:py-32 lg:py-40 px-6 md:px-12 lg:px-20 bg-watercolor-soft relative">
        <CornerLeaves className="absolute top-12 left-4 w-28 opacity-50" />
        <CornerLeaves className="absolute bottom-12 right-4 w-28 opacity-50" flip />
        <div className="max-w-[1440px] mx-auto grid lg:grid-cols-12 gap-12 lg:gap-20 items-start relative">
          <motion.div {...fadeUp} className="lg:col-span-5 lg:sticky lg:top-32">
            <div className="gold-corner p-5 bg-ivory">
              <div className="img-hover aspect-[4/5]">
                <img src={settings?.about_image_url} alt="Tammy at work" />
              </div>
            </div>
          </motion.div>
          <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.2 }} className="lg:col-span-7 lg:pt-12">
            <p className="overline text-rose mb-4">The Artisan</p>
            <p className="font-pinyon text-5xl md:text-7xl text-rose leading-[0.85]">Meet Tammy</p>
            <p className="font-display italic text-xl md:text-2xl text-ink-soft mt-6">A late-bloom love letter to the craft of patisserie.</p>
            <HeartDivider className="w-40 h-5 my-8" />
            <p className="text-base md:text-lg text-ink-soft font-light leading-[1.85] whitespace-pre-line">
              {settings?.about_text}
            </p>
          </motion.div>
        </div>
      </section>

      <HeartDivider className="w-64 h-5 mx-auto my-6" />

      {/* ======= GALLERY PREVIEW ======= */}
      <section id="gallery" data-testid="gallery-section" className="py-24 md:py-32 lg:py-40 px-6 md:px-12 lg:px-20 bg-ivory">
        <div className="max-w-[1440px] mx-auto">
          <SectionTitle
            overline="The Portfolio"
            script="The Gallery"
            title="A Garden of Past Creations"
          />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {gallery.slice(0, 8).map((g, i) => (
              <motion.div
                key={g.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 1, delay: (i % 4) * 0.08, ease: [0.16, 1, 0.3, 1] }}
                className={`img-hover ${i % 5 === 0 ? "row-span-2 aspect-[3/5]" : "aspect-square"}`}
                data-testid={`gallery-item-${i}`}
              >
                <img src={g.image_url} alt={g.caption} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ======= HOW ORDERING WORKS ======= */}
      <section id="ordering" data-testid="ordering-section" className="py-24 md:py-32 lg:py-40 px-6 md:px-12 lg:px-20 bg-watercolor relative overflow-hidden">
        <CornerLeaves className="absolute top-8 right-6 w-36 opacity-50" flip />
        <CornerLeaves className="absolute bottom-8 left-6 w-36 opacity-50" />
        <div className="max-w-[1440px] mx-auto relative">
          <SectionTitle
            overline="The Process"
            script="How It Works"
            title="A Quiet, Six-Step Conversation"
            sub="No checkout, no rush. Just a gentle dialogue about the celebration you're creating."
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { n: "01", t: "Choose Your Product", d: "Cupcake bouquet or boxed floral arrangement — pick the form your vision takes." },
              { n: "02", t: "Choose a Size", d: "From a 3-piece keepsake to a 19-piece statement centerpiece." },
              { n: "03", t: "Choose Your Flavors", d: "Cake, fillings, and premium upgrades — built around your taste." },
              { n: "04", t: "Share Your Colors", d: "Describe your desired palette in your own words, or share inspiration photos." },
              { n: "05", t: "Select Your Date", d: "Choose from available dates — we limit orders to give each piece full attention." },
              { n: "06", t: "Submit Your Inquiry", d: "We review your request and respond within 24 hours to confirm pricing." },
            ].map((s, i) => (
              <motion.div key={i} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.08 }} data-testid={`step-${i + 1}`}
                className="frame-gold p-8 text-center">
                <p className="font-script text-5xl text-rose leading-none">{s.n}</p>
                <Sprig className="w-16 h-5 mx-auto my-4 opacity-70" />
                <h3 className="font-display text-xl md:text-2xl text-ink">{s.t}</h3>
                <p className="text-sm text-ink-soft font-light leading-relaxed mt-3">{s.d}</p>
              </motion.div>
            ))}
          </div>
          <motion.div {...fadeUp} className="mt-20 text-center">
            <p className="font-display italic text-xl md:text-2xl text-ink-soft max-w-xl mx-auto leading-relaxed">
              &ldquo;We will contact you to confirm availability and final pricing.&rdquo;
            </p>
            <Link to="/inquiry" data-testid="ordering-cta" className="btn-pill-pink mt-10 inline-flex">
              <span className="heart" /> Begin Your Inquiry
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ======= TESTIMONIALS ======= */}
      <section id="testimonials" data-testid="testimonials-section" className="py-24 md:py-32 lg:py-40 px-6 md:px-12 lg:px-20 bg-cream-soft">
        <div className="max-w-[1440px] mx-auto">
          <SectionTitle overline="Kind Words" script="Love Notes" title="From the Table" />
          <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
            {testimonials.map((t, i) => (
              <motion.figure
                key={t.id}
                {...fadeUp}
                transition={{ ...fadeUp.transition, delay: i * 0.1 }}
                className="frame-gold p-8 text-center"
                data-testid={`testimonial-${i}`}
              >
                <div className="flex gap-1 justify-center mb-5">
                  {Array.from({ length: t.rating || 5 }).map((_, k) => (
                    <Star key={k} size={14} strokeWidth={1.25} className="fill-rose text-rose" />
                  ))}
                </div>
                <blockquote className="font-display italic text-lg md:text-xl text-ink leading-[1.5]">
                  &ldquo;{t.quote}&rdquo;
                </blockquote>
                <Sprig className="w-16 h-5 mx-auto my-6 opacity-70" />
                <figcaption>
                  {t.photo_url && <img src={t.photo_url} alt={t.customer_name} className="w-12 h-12 object-cover rounded-full mx-auto mb-3" />}
                  <p className="font-script text-2xl text-rose leading-none">{t.customer_name}</p>
                  <p className="overline text-ink-soft mt-2">{t.event_type}</p>
                </figcaption>
              </motion.figure>
            ))}
          </div>
          <motion.div {...fadeUp} className="mt-16 text-center">
            <p className="font-display italic text-lg text-ink-soft mb-6">Have we baked for you? We'd love to hear about it.</p>
            <button onClick={() => setReviewOpen(true)} className="btn-pill-pink" data-testid="leave-review-btn">
              <Star size={14} strokeWidth={1.5} /> Leave a Review
            </button>
          </motion.div>
        </div>
      </section>

      <ReviewModal
        open={reviewOpen}
        onClose={() => setReviewOpen(false)}
        onSuccess={() => api.get("/testimonials").then(r => setTestimonials(r.data)).catch(() => {})}
      />

      {/* ======= CONTACT ======= */}
      <section id="contact" data-testid="contact-section" className="py-24 md:py-32 lg:py-40 px-6 md:px-12 lg:px-20 bg-watercolor relative overflow-hidden">
        <CornerLeaves className="absolute top-10 left-6 w-32 opacity-50" />
        <CornerLeaves className="absolute bottom-10 right-6 w-32 opacity-50" flip />
        <div className="max-w-[1100px] mx-auto text-center relative">
          <SectionTitle
            overline="The Studio"
            script="Say Hello"
            title="Begin a Conversation"
            sub="Every Tammy's Treats creation starts with a quiet inquiry. Reach out — we read every message."
          />
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            <div className="frame-gold p-8" data-testid="contact-phone">
              <Phone size={22} strokeWidth={1.25} className="text-rose mx-auto" />
              <p className="overline mt-5">Phone</p>
              <a href={`tel:${settings?.phone}`} className="font-display text-lg mt-3 block text-ink">{settings?.phone}</a>
            </div>
            <div className="frame-gold p-8" data-testid="contact-email">
              <Mail size={22} strokeWidth={1.25} className="text-rose mx-auto" />
              <p className="overline mt-5">Email</p>
              <a href={`mailto:${settings?.email}`} className="font-display text-lg mt-3 block break-all text-ink">{settings?.email}</a>
            </div>
            <div className="frame-gold p-8" data-testid="contact-location">
              <MapPin size={22} strokeWidth={1.25} className="text-rose mx-auto" />
              <p className="overline mt-5">Studio</p>
              <p className="font-display text-lg mt-3 text-ink">{settings?.location}</p>
            </div>
          </div>

          {settings?.delivery_enabled && (
            <div className="mt-16 frame-gold p-10 md:p-14 text-left">
              <div className="text-center">
                <p className="overline text-rose">Delivery & Pickup</p>
                <p className="font-script text-4xl md:text-5xl text-rose leading-none mt-2">Bringing the Bouquet to You</p>
              </div>
              <SectionFlourish className="w-40 h-5 mx-auto my-8" />
              <div className="grid md:grid-cols-2 gap-10 text-sm text-ink-soft font-light leading-relaxed">
                <div>
                  <p className="overline mb-3 text-rose">Delivery</p>
                  <p>Available within {settings.delivery_radius_miles} miles of Mooresville.</p>
                  <p className="mt-3">{settings.delivery_fee}</p>
                </div>
                <div>
                  <p className="overline mb-3 text-rose">Pickup</p>
                  <p>{settings.pickup_instructions}</p>
                  <p className="mt-3 font-medium text-ink">{settings.pickup_hours}</p>
                </div>
              </div>
            </div>
          )}

          {settings?.venmo_enabled && settings?.venmo_handle && (
            <div className="mt-8 frame-gold p-10 md:p-12 text-center">
              <p className="overline text-rose">Payment</p>
              <p className="font-pinyon text-5xl md:text-6xl text-rose leading-none mt-2">Pay via Venmo</p>
              <SectionFlourish className="w-40 h-5 mx-auto my-6" />
              <p className="text-base text-ink-soft font-light max-w-md mx-auto leading-relaxed">
                Once your order is confirmed, settle up with a quick Venmo transfer. Fast, secure, and easy — no need for cash at pickup.
              </p>
              <a
                href={settings.venmo_url || `https://venmo.com/u/${settings.venmo_handle.replace(/^@/, "")}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="venmo-link"
                className="btn-pill-pink mt-8"
                style={{ background: "#3D95CE", borderColor: "#3D95CE" }}
              >
                <Wallet size={14} strokeWidth={1.5} /> Pay {settings.venmo_handle}
              </a>
              <p className="text-xs text-ink-soft mt-5 italic">Payment collected only after order confirmation.</p>
            </div>
          )}

          <Link to="/inquiry" data-testid="contact-cta" className="btn-pill-pink mt-12 inline-flex">
            <span className="heart" /> Submit an Inquiry
          </Link>
        </div>
      </section>

      {/* ======= INSTAGRAM FEED ======= */}
      {instagram.length > 0 && (
        <section data-testid="instagram-section" className="py-24 px-6 md:px-12 lg:px-20 bg-ivory">
          <div className="max-w-[1440px] mx-auto">
            <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
              <div>
                <p className="overline text-rose mb-3">Follow Along</p>
                <p className="font-script text-5xl md:text-6xl text-rose leading-none">on Instagram</p>
                <p className="font-display text-2xl text-ink mt-3">{settings?.instagram_handle}</p>
              </div>
              {settings?.instagram_url && (
                <a href={settings.instagram_url} target="_blank" rel="noopener noreferrer" data-testid="ig-follow" className="btn-pill-pink">
                  <Instagram size={14} strokeWidth={1.5} /> Follow
                </a>
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {instagram.slice(0, 6).map((g, i) => (
                <motion.a
                  key={g.id}
                  href={settings?.instagram_url || "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: i * 0.05 }}
                  className="img-hover aspect-square"
                  data-testid={`ig-item-${i}`}
                >
                  <img src={g.image_url} alt={g.caption} />
                </motion.a>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer settings={settings} />
    </div>
  );
}
